import type {AddBlockedByInput} from '@github-ui/server-persisted-queries/types/createIssueAddBlockedByMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {AddBlockedByResult} from '../utils/relationship-helpers'
import type {
  AddBlockedByInput as RelayAddBlockedByInput,
  createIssueAddBlockedByMutation,
  createIssueAddBlockedByMutation$data,
} from './__generated__/createIssueAddBlockedByMutation.graphql'

export function commitCreateIssueAddBlockedByMutation({
  environment,
  input,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: RelayAddBlockedByInput
  onError?: (error: Error) => void
  onCompleted?: (response: createIssueAddBlockedByMutation$data) => void
}) {
  return commitMutation<createIssueAddBlockedByMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createIssueAddBlockedByMutation($input: AddBlockedByInput!) @raw_response_type {
        addBlockedBy(input: $input) {
          issue {
            id
          }
          blockingIssue {
            id
          }
          errors {
            message
          }
          # Note: We don't fetch the updated blocked/blockedBy relationships here
          # because this mutation runs fire-and-forget after issue creation completes.
          # The user is redirected to the new issue page, which will fetch fresh data.
        }
      }
    `,
    variables: {
      input,
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}

export function relayCreateIssueAddBlockedBy(
  environment: Environment,
  input: AddBlockedByInput,
): Promise<AddBlockedByResult> {
  return new Promise((resolve, reject) => {
    commitCreateIssueAddBlockedByMutation({
      environment,
      input: {
        issueId: String(input.issueId),
        blockingIssueId: String(input.blockingIssueId),
        clientMutationId: input.clientMutationId,
      },
      onError: reject,
      onCompleted: response => {
        if (response.addBlockedBy?.errors.length) {
          reject(new Error(response.addBlockedBy.errors[0]?.message ?? 'Error in request'))
          return
        }

        if (!response.addBlockedBy?.issue || !response.addBlockedBy.blockingIssue) {
          reject(new Error('Error in request'))
          return
        }

        resolve({
          issueId: response.addBlockedBy.issue.id,
          blockingIssueId: response.addBlockedBy.blockingIssue.id,
        })
      },
    })
  })
}
